import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  RotateCcw, Package, Clock, CheckCircle, AlertCircle, 
  Truck, CreditCard, HelpCircle, ChevronDown, ChevronUp,
  FileText, ShieldCheck, XCircle, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageSeo } from '@/components/seo/PageSeo';

// FAQ Item Component
const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors"
      data-testid="faq-toggle"
    >
      <span className="font-medium text-gray-900">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-gray-500 shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-500 shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <p className="text-gray-600 text-sm">{answer}</p>
      </div>
    )}
  </div>
);

// Step Card Component
const StepCard = ({ number, icon: Icon, title, description }) => (
  <div className="relative bg-white rounded-xl p-6 shadow-sm border border-gray-100">
    <div className="absolute -top-3 -left-3 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm">
      {number}
    </div>
    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-primary" />
    </div>
    <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-600">{description}</p>
  </div>
);

const ReturnsPage = () => {
  const { t } = useTranslation();
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const returnSteps = [
    {
      icon: FileText,
      title: t('returns.step1Title'),
      description: t('returns.step1Desc')
    },
    {
      icon: Package,
      title: t('returns.step2Title'),
      description: t('returns.step2Desc')
    },
    {
      icon: Truck,
      title: t('returns.step3Title'),
      description: t('returns.step3Desc')
    },
    {
      icon: CreditCard,
      title: t('returns.step4Title'),
      description: t('returns.step4Desc')
    }
  ];

  const faqs = [
    {
      question: t('returns.faq1Question'),
      answer: t('returns.faq1Answer')
    },
    {
      question: t('returns.faq2Question'),
      answer: t('returns.faq2Answer')
    },
    {
      question: t('returns.faq3Question'),
      answer: t('returns.faq3Answer')
    },
    {
      question: t('returns.faq4Question'),
      answer: t('returns.faq4Answer')
    },
    {
      question: t('returns.faq5Question'),
      answer: t('returns.faq5Answer')
    }
  ];

  const eligibleItems = [
    t('returns.eligible1'),
    t('returns.eligible2'),
    t('returns.eligible3'),
    t('returns.eligible4')
  ];

  const nonEligibleItems = [
    t('returns.nonEligible1'),
    t('returns.nonEligible2'),
    t('returns.nonEligible3'),
    t('returns.nonEligible4')
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="returns-page">
      <PageSeo title={t('returns.pageTitle')} description={t('returns.heroDescription')} segments={['returns']} />
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-primary transition-colors">
              {t('common.home')}
            </Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">{t('returns.pageTitle')}</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-white py-12">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-4 flex items-center gap-3">
              <RotateCcw className="w-10 h-10" />
              {t('returns.pageTitle')}
            </h1>
            <p className="text-lg text-white/90">
              {t('returns.heroDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-8 py-12">
        
        {/* Key Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">14 {t('returns.days')}</h3>
            <p className="text-sm text-gray-600">{t('returns.returnPeriod')}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Truck className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{t('returns.free')}</h3>
            <p className="text-sm text-gray-600">{t('returns.freeShipping')}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">3-5 {t('returns.days')}</h3>
            <p className="text-sm text-gray-600">{t('returns.refundTime')}</p>
          </div>
        </div>

        {/* Return Process Steps */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <ArrowRight className="w-6 h-6 text-primary" />
            {t('returns.howToReturn')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {returnSteps.map((step, index) => (
              <StepCard
                key={index}
                number={index + 1}
                icon={step.icon}
                title={step.title}
                description={step.description}
              />
            ))}
          </div>
        </section>

        {/* Eligible / Non-Eligible Items */}
        <section className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Eligible Items */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-green-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{t('returns.eligibleTitle')}</h3>
              </div>
              <ul className="space-y-3">
                {eligibleItems.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Non-Eligible Items */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-red-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{t('returns.nonEligibleTitle')}</h3>
              </div>
              <ul className="space-y-3">
                {nonEligibleItems.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                    <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Exchange Information */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-blue-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                <RotateCcw className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('returns.exchangeTitle')}</h3>
                <p className="text-gray-600 mb-4">{t('returns.exchangeDescription')}</p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-500" />
                    {t('returns.exchange1')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-500" />
                    {t('returns.exchange2')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-500" />
                    {t('returns.exchange3')}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-primary" />
            {t('returns.faqTitle')}
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => toggleFAQ(index)}
              />
            ))}
          </div>
        </section>

        {/* Contact CTA */}
        <section>
          <div className="bg-gray-900 rounded-xl p-6 md:p-8 text-center">
            <ShieldCheck className="w-12 h-12 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">{t('returns.needHelp')}</h3>
            <p className="text-gray-400 mb-6 max-w-lg mx-auto">{t('returns.needHelpDesc')}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <Button className="px-6" data-testid="contact-button">
                  {t('footer.contactUs')}
                </Button>
              </Link>
              <Link to="/orders">
                <Button variant="outline" className="px-6 border-gray-600 text-white hover:bg-gray-800" data-testid="orders-button">
                  {t('returns.myOrders')}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ReturnsPage;
